import { useCallback, useEffect, useState } from 'react';
import { Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Link } from '@mui/material';
import CommonListTable, { CommonListColumn } from '../Common/CommonListTable';
import { CookService } from '../../services/CookServices';
import EventBus from '../../EventBus';
import { EventBusEventsEnum } from '../../enums/EventBusEventsEnum';
import { CustomSnackbarTypes } from '../Common/CustomSnackBar';

interface InputProps {
    cookId: number;
}

const cookService = new CookService();

const COLUMNS: CommonListColumn[] = [
    {
        label: 'Cook Id',
        render: (row) => (
            <Link href={`/#/Cooks/${row.referredCookId}/show`}>{row?.referredCookId}</Link>
        ),
    },
    {
        label: 'Name',
        render: (row) =>
            `${row?.referredCook?.firstName ?? ''} ${row?.referredCook?.lastName ?? ''}`.trim() || '-',
    },
    { label: 'Onboarding Status', render: (row) => row?.referredCook?.onboardingStatus ?? '-' },
    {
        label: 'Payout Status',
        render: (row) => (row?.referalAmountProcessedAt ? 'PROCESSED' : 'NOT-PROCESSED'),
    },
];

const CookReferralTab = ({ cookId }: InputProps) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');

    const refreshData = useCallback(() => {
        setLoading(true);
        cookService
            .getCookReferrals(rowsPerPage, page * rowsPerPage, { order: 'DESC', field: 'id' }, { referralCookId: cookId })
            .then((res: any) => {
                setData(res?.data ?? []);
                setTotal(res?.total ?? 0);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [cookId, rowsPerPage, page]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    const handleSubmit = () => {
        if (name.trim() === '' || phone.trim() === '') {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Enter all details to continue',
                type: CustomSnackbarTypes.WARNING,
            });
            return;
        }
        if (phone.length < 10) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Mobile number must be of 10 digits',
                type: CustomSnackbarTypes.WARNING,
            });
            return;
        }
        cookService
            .createCookReferral({ referralCookId: cookId, fname: name, lname: lastName, phoneNumber: phone })
            .then(() => {
                refreshData();
                setOpen(false);
                setName('');
                setPhone('');
                setLastName('');
            });
    };

    return (
        <div>
            <Box display="flex" flexDirection="row-reverse" my={2}>
                <Button size="small" variant="contained" onClick={() => setOpen(true)}>
                    Add new referred cook
                </Button>
            </Box>
            <CommonListTable
                columns={COLUMNS}
                data={data}
                loading={loading}
                page={page}
                rowsPerPage={rowsPerPage}
                total={total}
                onPageChange={setPage}
                onRowsPerPageChange={(rows) => { setRowsPerPage(rows); setPage(0); }}
                emptyText="No Cook Referral"
            />
            {open && (
                <Dialog open={open} onClose={() => setOpen(false)}>
                    <DialogTitle>Add Cook Referral</DialogTitle>
                    <DialogContent>
                        <Box display="flex" flexDirection="column">
                            <TextField label="First Name" margin="dense" value={name} onChange={(e) => setName(e.target.value)} />
                            <TextField label="Last Name" margin="dense" value={lastName} required={false} onChange={(e) => setLastName(e.target.value)} />
                            <TextField type="number" label="Mobile Number" margin="dense" value={phone} onChange={(e) => setPhone(e.target.value)} />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button size="small" variant="outlined" onClick={() => setOpen(false)}>close</Button>
                        <Button size="small" variant="contained" color="error" onClick={handleSubmit}>Submit</Button>
                    </DialogActions>
                </Dialog>
            )}
        </div>
    );
};

export default CookReferralTab;
